import { animated, useSpring } from '@react-spring/web';
import {
  Card,
  ChevronRight,
  Plus,
  Typography,
  useMobile,
  useMobileAndTablet,
} from '@sonnen/web-ui';
import clsx from 'clsx';
import { createElement } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import Tariff700 from '../../assets/images/AddTariff_mobile.webp';
import Tariff1400 from '../../assets/images/AddTariff_wide.webp';
import SelfConsumption700 from '../../assets/images/SelfConsumption_mobile.webp';
import SelfConsumption1400 from '../../assets/images/SelfConsumption_wide.webp';
import { AppRoute } from '../../constants';
import { useTopScrollNavigate } from '../../hooks/useTopScrollNavigate';
import { useTranslation } from '../../i18n/i18n';
import { OrderFlow } from '../../types';
import CroppedImage from '../CroppedImage/CroppedImage';
import styles from './OptimizationCard.module.css';

export const i18nScope = 'energyStore.optimization';

const VARIANTS = {
  batteryOptimization: {
    src: Tariff700,
    srcSet: `${Tariff700} 700w, ${Tariff1400} 1400w`,
    icon: Plus,
    route: AppRoute.BATTERY_OPTIMIZATION,
  },
  selfConsumption: {
    src: SelfConsumption700,
    srcSet: `${SelfConsumption700} 700w, ${SelfConsumption1400} 1400w`,
    icon: ChevronRight,
    route: AppRoute.SELF_CONSUMPTION_PLUS,
  },
};

type OptimizationCardProps = {
  scope: 'batteryOptimization' | 'selfConsumption';
};

const OptimizationCard = ({ scope }: OptimizationCardProps) => {
  const { t } = useTranslation();
  const mobile = useMobile();
  const tablet = useMobileAndTablet();
  const navigate = useTopScrollNavigate();
  const { setOrderFlow } = useAppContext();

  const props = useSpring({
    from: { scale: 0.95, opacity: 0 },
    to: { scale: 1, opacity: 1 },
    config: { tension: 200, friction: 20 },
    delay: 100,
  });

  const { src, srcSet, icon, route } = VARIANTS[scope];

  const onClick = () => {
    setOrderFlow(
      scope === 'batteryOptimization'
        ? OrderFlow.batteryOptimization
        : OrderFlow.selfConsumptionPlus,
    );
    navigate(route);
  };

  const imageHeight = tablet && !mobile ? 420 : 180;
  const descriptionId = `${scope}-text`;

  return (
    <animated.button
      style={props}
      className={styles.optimizationCard}
      data-testid={`go-to-${scope}-button`}
      onClick={onClick}
      aria-describedby={descriptionId}
    >
      <Card
        paddingSize="none"
        dataTestId={`${scope}-optimization`}
        className={clsx(styles.optimizationCard)}
        withBottomSpace={false}
      >
        <CroppedImage
          height={imageHeight}
          src={src}
          srcSet={srcSet}
          dataTestId={`${scope}-image`}
          objectPosition="center center"
          noBottomRadius
        />

        <Card className={styles.titleAndText} withBottomSpace={false} paddingSize="md">
          <Typography.H4>{t(`${i18nScope}.${scope}.title`)}</Typography.H4>
          <div className={styles.textAndIcon} id={descriptionId}>
            <Typography.Body1 variant="secondary">
              {t(`${i18nScope}.${scope}.text`)}
            </Typography.Body1>
            <div>{createElement(icon, { color: 'secondary' })}</div>
          </div>
        </Card>
      </Card>
    </animated.button>
  );
};

export default OptimizationCard;
